import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { API } from "../shared";
import { MonthView } from "./CalendarViews";
import WeekGrid from "./WeekGrid";
import DayPanel from "./DayPanel";
import { addDays, bucket, dayLabel, monthGridStart, todayCT, weekStart } from "@/lib/calendar";

const pill = (on) => `rounded-full px-4 py-2 text-sm font-bold transition-colors ${on ? "bg-[#2563EB] text-white" : "border border-white/15 text-white/60 hover:text-white"}`;

export default function LessonCalendar({ token, types, onChanged, version }) {
  const [view, setView] = useState("week");
  const [initTime, setInitTime] = useState("");
  const [anchor, setAnchor] = useState(todayCT());
  const [selected, setSelected] = useState(todayCT());
  const [cal, setCal] = useState(null);
  const [msg, setMsg] = useState({ ok: "", err: "" });
  const h = useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);
  const start = view === "week" ? weekStart(anchor) : monthGridStart(anchor);
  const count = view === "week" ? 7 : 42;
  const days = useMemo(() => Array.from({ length: count }, (_, i) => addDays(start, i)), [start, count]);
  const load = useCallback(() => axios.get(`${API}/admin/calendar`, { ...h, params: { start, days: count } }).then((r) => setCal(r.data)), [h, start, count]);
  useEffect(() => { setCal(null); setMsg({ ok: "", err: "" }); load(); }, [load]);
  useEffect(() => { if (version) load(); }, [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = (fn) => async (path, body, ok) => {
    setMsg({ ok: "", err: "" });
    try { const r = await fn(path, body); setMsg({ ok: typeof ok === "function" ? ok(r.data) : ok || "Saved.", err: "" }); await load(); onChanged?.(); return r.data || true; }
    catch (e) { const d = e?.response?.data?.detail; setMsg({ ok: "", err: typeof d === "string" ? d : "Could not save." }); return e?.response?.status === 409 && path === "/admin/lessons-manual" ? 409 : false; }
  };
  const api = {
    post: run((p, b) => axios.post(`${API}${p}`, b, h)),
    put: run((p, b) => axios.put(`${API}${p}`, b, h)),
    del: run((p) => axios.delete(`${API}${p}`, h)),
    raw: (p, params) => axios.get(`${API}${p}`, { ...h, params }).then((r) => r.data),
  };
  const pick = (d, t = "") => { setSelected(d); setInitTime(t); };
  const shift = (n) => setAnchor(view === "week" ? addDays(anchor, 7 * n) : addDays(`${anchor.slice(0, 7)}-15`, 30 * n));
  const buckets = cal ? bucket(cal, days) : null;
  const title = view === "week" ? `${dayLabel(days[0], { month: "short", day: "numeric" })} – ${dayLabel(days[6], { month: "short", day: "numeric", year: "numeric" })}` : dayLabel(`${anchor.slice(0, 7)}-15`, { month: "long", year: "numeric" });

  return (
    <div data-testid="admin-calendar" className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-full border border-white/15 p-2 text-white/70 hover:text-white" aria-label="Previous"><ChevronLeft size={16} /></button>
          <button data-testid="cal-today" onClick={() => { setAnchor(todayCT()); setSelected(todayCT()); }} className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-bold text-white/70 hover:text-white">Today</button>
          <button data-testid="cal-next" onClick={() => shift(1)} className="rounded-full border border-white/15 p-2 text-white/70 hover:text-white" aria-label="Next"><ChevronRight size={16} /></button>
          <p data-testid="cal-title" className="ml-1 text-base font-extrabold text-white">{title}</p>
        </div>
        <div className="flex gap-2"><button data-testid="cal-view-week" onClick={() => setView("week")} className={pill(view === "week")}>Week</button><button data-testid="cal-view-month" onClick={() => setView("month")} className={pill(view === "month")}>Month</button></div>
      </div>
      <p className="mt-2 text-xs text-white/40">All times Central. Click an empty spot (or a day) to add availability, block time, book a lesson or repeat a schedule.</p>
      {msg.ok && <p data-testid="lessons-note" className="mt-3 text-sm font-semibold text-emerald-300">{msg.ok}</p>}
      {msg.err && <p data-testid="lessons-msg" className="mt-3 text-sm font-semibold text-red-300">{msg.err}</p>}
      <div className="mt-4">
        {!buckets && <Loader2 className="animate-spin text-white/40" />}
        {buckets && view === "week" && <WeekGrid days={days} buckets={buckets} eff={cal.effective} selected={selected} onSelect={pick} />}
        {buckets && view === "month" && <MonthView days={days} month={anchor.slice(0, 7)} buckets={buckets} selected={selected} onSelect={(d) => pick(d)} />}
      </div>
      {buckets && buckets[selected] && <DayPanel key={`${selected}-${initTime}`} day={selected} b={buckets[selected]} eff={cal.effective?.[selected]} types={types} api={api} initTime={initTime} />}
    </div>
  );
}
